// React & Hooks
import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Tabulator } from 'react-tabulator/lib/types/TabulatorTypes';
import type { AppVersionListItem } from '@/domains/system-management/apis/app-version';
import { useAppVersion } from '@/domains/system-management/hooks/useAppVersion';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';
import Button from '@/shared/components/ui/Button';
import ButtonGroup from '@/shared/components/ui/ButtonGroup/ButtonGroup';
// Components
import ContainerCard from '@/shared/components/ui/ContainerCard/ContainerCard';
import DetailForm from '@/shared/components/ui/DetailForm/DetailForm';
import Loader from '@/shared/components/ui/Loader/Loader';
import {
  type ColumnDefinition,
  ReactTabulator,
} from '@/shared/components/ui/LocalizedTabulator/LocalizedTabulator';
import LogList from '@/shared/components/ui/LogList/LogList';
import { URLS } from '@/shared/config/urls';

// ---------------------------------------------------------------------------
// UI section (Title / logos / login background / event types / camera stream
// + Save / Cancel) is HIDDEN - the code is kept in comments, not deleted.
// To restore it: uncomment every "UI section" block in this file (imports,
// constants, state / handlers, and the JSX card above the Mobile App card).
// ---------------------------------------------------------------------------
// UI section - imports
// import { useCallback } from 'react';
// import { usePreferenceManagement } from '@/domains/system-management/hooks/usePreferenceManagement';
// import type {
//   ButtonState,
//   Files,
//   FormState,
//   Preference,
//   PreferenceFileRequest,
//   PreferenceFileResponse,
//   PreferenceUpdateItem,
//   UpdatePreferenceRequest,
// } from '@/domains/system-management/types/preference';
// import CheckBox from '@/shared/components/ui/CheckBox/CheckBox';
// import useCommonCodes from '@/shared/hooks/useCommonCodes';

// UI section - constants
// The 8 traffic event types this page lets an admin toggle - a fixed subset of
// cm_code group EVT_TP (the group also holds box/sensor events like
// temperature and door-open, which do not belong on this checklist). Order is
// the display order; labels come from cm_code, not hardcoded here, so they
// follow the operator's language like every other screen.
// const TRAFFIC_EVENT_TYPE_CODES = [
//   'EVT_TP_WWD',
//   'EVT_TP_SPD',
//   'EVT_TP_JW',
//   'EVT_TP_ILP',
//   'EVT_TP_SLV',
//   'EVT_TP_STP',
//   'EVT_TP_PED',
//   'EVT_TP_EXC',
// ] as const;
//
// const EVENT_TYPE_UPPER_CODE = 'EVT_TP';

// Mirrors the backend cap (upload_app_version.MAX_APK_BYTES) and nginx's
// client_max_body_size for /api - a bigger file would be rejected there.
const APK_MAX_BYTES = 500 * 1024 * 1024;

// Same shape the backend accepts: dotted numbers, optional leading v.
const APP_VERSION_PATTERN = /^v?\d+(\.\d+){0,3}$/;

// Download of one release's APK (GET /app-version/download/{app_id}, login
// required). Every live row is downloadable, not only the latest one; the
// public landing page keeps using /app-version/download/latest.
const apkDownloadUrl = (appId: number) => `${URLS.BACK_DSH}/app-version/download/${appId}`;

const formatFileSize = (bytes: number | null) => {
  if (bytes == null) return '-';
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// released_at is ISO 8601 in WIB from the backend; show date + time only.
// String slicing, not a Date object - re-parsing through new Date() would
// reinterpret the string via the browser's own timezone, when it's already
// WIB as-is. Display format DD/MM/YYYY (Audrey), time left untouched.
const formatReleasedAt = (iso: string | null) => {
  if (!iso) return '-';
  const [datePart, timePart] = iso.slice(0, 19).split('T');
  const [year, month, day] = (datePart ?? '').split('-');
  if (!year || !month || !day) return '-';
  return `${day}/${month}/${year} ${timePart ?? ''}`.trim();
};

// Tabulator custom formatters return HTML strings, so anything that came from
// a user (file name) is escaped before it is put in a cell.
const escapeHtml = (value: string) =>
  value.replace(
    /[&<>"']/g,
    (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch] ?? ch
  );

// Stable empty list so react-tabulator does not rebuild the table on every
// render while the release list is still loading.
const EMPTY_RELEASES: AppVersionListItem[] = [];

// Same column defaults as Language Management's table.
const commonColumnSettings = {
  hozAlign: 'center' as const,
  headerHozAlign: 'center' as const,
  resizable: false,
};

const PreferencesManagement = () => {
  const { t, i18n } = useTranslation();

  // Mobile App (.apk) releases - table + form CRUD, the same state machine as
  // LanguageManagement.tsx (row click -> edit, New -> register, Cancel/Delete).
  const tbRef = useRef<Tabulator | null>(null);
  const versionRef = useRef<HTMLInputElement | null>(null);
  const apkFileRef = useRef<HTMLInputElement | null>(null);

  const hasChangesUpdateRef = useRef<boolean>(false);
  const hasChangesCreateRef = useRef<boolean>(false);
  const isNewClickedRef = useRef<boolean>(false);
  const selectedIdRef = useRef<number | null>(null);

  const [disabledForm, setDisabledForm] = useState<boolean>(true);
  const [hasChangesUpdate, setHasChangesUpdate] = useState<boolean>(false);
  const [hasChangesCreate, setHasChangesCreate] = useState<boolean>(false);
  const [isNewClicked, setIsNewClicked] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [newId, setNewId] = useState<number | null>(null);

  // What the operator typed; the file is either the one attached now (apkFile)
  // or, on an existing release, the stored one (storedFileName).
  const [formValues, setFormValues] = useState({ version: '', notes: '' });
  const [apkFile, setApkFile] = useState<File | null>(null);
  const [storedFileName, setStoredFileName] = useState<string>('');

  const [buttonState, setButtonState] = useState({
    confirm: true,
    cancel: true,
    delete: true,
    create: false,
  });

  const { useAppVersionList, useUploadAppVersion, useUpdateAppVersion, useDeleteAppVersion } =
    useAppVersion();
  const { data: releaseData } = useAppVersionList();
  const releases = releaseData?.data?.items ?? EMPTY_RELEASES;
  const selectedRelease = releases.find((item) => item.app_id === selectedId) ?? null;

  const clearFileInput = () => {
    setApkFile(null);
    if (apkFileRef.current) apkFileRef.current.value = '';
  };

  const emptyDetail = () => {
    setFormValues({ version: '', notes: '' });
    setStoredFileName('');
    clearFileInput();
  };

  const disableAllButtons = () =>
    setButtonState({ confirm: true, cancel: true, delete: true, create: true });

  const enableInitialButtons = () => {
    disableAllButtons();
    setIsNewClicked(false);
    setButtonState((prev) => ({ ...prev, create: false }));
  };
  const enableUpdateButtons = () => {
    disableAllButtons();
    setButtonState((prev) => ({ ...prev, cancel: false, delete: false, create: false }));
  };
  const enableRegisterButtons = () => {
    disableAllButtons();
    setButtonState((prev) => ({ ...prev, cancel: false }));
  };

  const reloadCallback = () => {
    enableInitialButtons();
    tbRef.current?.deselectRow();
    emptyDetail();
    setDisabledForm(true);
    setHasChangesUpdate(false);
    setHasChangesCreate(false);
    setSelectedId(null);
    setNewId(null);
  };

  const uploadAppVersionMutation = useUploadAppVersion((created) => {
    reloadCallback();
    setNewId(created?.app_id ?? null);
  });
  const updateAppVersionMutation = useUpdateAppVersion((updated) => {
    enableUpdateButtons();
    setHasChangesUpdate(false);
    setStoredFileName(updated?.apk_file_name ?? '');
    clearFileInput();
  });
  const deleteAppVersionMutation = useDeleteAppVersion(() => {
    reloadCallback();
    setNewId(null);
  });

  useEffect(() => {
    hasChangesUpdateRef.current = hasChangesUpdate;
    hasChangesCreateRef.current = hasChangesCreate;
    isNewClickedRef.current = isNewClicked;
    selectedIdRef.current = selectedId;
  }, [hasChangesUpdate, hasChangesCreate, isNewClicked, selectedId]);

  // ------------------------------------------------------------------------
  // UI section - state, queries and handlers (hidden, kept for reference)
  // ------------------------------------------------------------------------
  // const loginLogoRef = useRef<HTMLInputElement | null>(null);
  // const navLogoRef = useRef<HTMLInputElement | null>(null);
  // const loginBackgroundRef = useRef<HTMLInputElement | null>(null);
  //
  // const [files, setFiles] = useState<Files>({
  //   login_logo: null,
  //   nav_logo: null,
  //   login_background: null,
  // });
  //
  // const [formData, setFormData] = useState<FormState>({
  //   title: '',
  //   event_types: [],
  //   camera_stream_enabled: true,
  // });
  //
  // const [preferenceMap, setPreferenceMap] = useState<Record<string, string>>({});
  // const [button, setButton] = useState<ButtonState>({
  //   submit: true,
  //   cancel: true,
  // });
  // const [preferences, setPreferences] = useState<Preference[]>([]);
  //
  // // Localized label per code, from cm_code (not hardcoded) - falls back to the
  // // raw code if a group fetch is still in flight or a code's label is empty.
  // const { commonListData: codeGroups } = useCommonCodes({
  //   optionParams: `upper-code=${EVENT_TYPE_UPPER_CODE}`,
  // });
  // const eventTypeGroup = codeGroups?.[EVENT_TYPE_UPPER_CODE];
  // const eventType = TRAFFIC_EVENT_TYPE_CODES.map((code) => {
  //   const index = eventTypeGroup?.code.indexOf(code) ?? -1;
  //   const label = index === -1 ? undefined : eventTypeGroup?.name[index];
  //   return { value: code, label: label || code };
  // });
  //
  // const { usePreference, useUpdatePreference, usePreferenceFile } = usePreferenceManagement();
  // const { data: preferenceData } = usePreference();
  //
  // const updatePreferenceMutation = useUpdatePreference(() => {
  //   setButton({ submit: true, cancel: true });
  // });
  // const uploadPreferenceFileMutation = usePreferenceFile((response: PreferenceFileResponse) => {
  //   const data = response.data;
  //   const grouped: Record<string, Preference> = {};
  //
  //   Object.entries(data).forEach(([key, value]) => {
  //     const baseKey = key.replace(/_(path|name)$/, '');
  //     if (!grouped[baseKey])
  //       grouped[baseKey] = {
  //         preference_key: baseKey,
  //         preference_value: '',
  //         preference_value2: '',
  //       };
  //
  //     if (key.endsWith('_path')) {
  //       grouped[baseKey].preference_value = value;
  //     } else if (key.endsWith('_name')) {
  //       grouped[baseKey].preference_value2 = value;
  //     }
  //   });
  //
  //   const newPreferences = Object.values(grouped);
  //
  //   setPreferences((prev) => {
  //     const filteredPrev = prev.filter(
  //       (p) => !newPreferences.some((np) => np.preference_key === p.preference_key)
  //     );
  //     return [...filteredPrev, ...newPreferences];
  //   });
  // });
  //
  // const handleFileChange = (
  //   e: ChangeEvent<HTMLInputElement>,
  //   type: keyof PreferenceFileRequest
  // ) => {
  //   const file = e.target.files?.[0];
  //   const maxSize = 5 * 1024 * 1024;
  //   const allowedTypes = ['image/svg+xml', 'image/png', 'image/jpeg'];
  //   if (file) {
  //     if (!allowedTypes.includes(file.type)) {
  //       // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
  //       new NoticeMessage(t('pref>msg>invalid file type'));
  //       return;
  //     }
  //     if (file.size > maxSize) {
  //       // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
  //       new NoticeMessage(t('pref>msg>file too large'));
  //       return;
  //     }
  //     setFiles((prev) => ({
  //       ...prev,
  //       [type]: file,
  //     }));
  //     setButton({ submit: false, cancel: false });
  //
  //     const formData = new FormData();
  //     formData.append(type, file);
  //     uploadPreferenceFileMutation.mutateAsync(formData);
  //   }
  // };
  //
  // const handleInputChange = (
  //   e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  // ) => {
  //   const { name, value } = e.target;
  //   setFormData((prev) => ({
  //     ...prev,
  //     [name]: value,
  //   }));
  //   setButton({ submit: false, cancel: false });
  // };
  //
  // const handleCameraToggleChange = (e: ChangeEvent<HTMLInputElement>) => {
  //   const { checked } = e.target;
  //   setFormData((prev) => ({
  //     ...prev,
  //     camera_stream_enabled: checked,
  //   }));
  //   setButton({ submit: false, cancel: false });
  // };
  //
  // const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
  //   const { value, checked } = e.target;
  //
  //   setFormData((prev) => {
  //     const newEventTypes = checked
  //       ? [...prev.event_types, value]
  //       : prev.event_types.filter((item) => item !== value);
  //
  //     return {
  //       ...prev,
  //       event_types: newEventTypes,
  //     };
  //   });
  //   setButton({ submit: false, cancel: false });
  // };
  //
  // const resetForm = useCallback(() => {
  //   if (!Array.isArray(preferenceData?.data) || preferenceData.data.length === 0) return;
  //
  //   const mapped = preferenceData.data.reduce<Record<string, string>>((acc, cur) => {
  //     acc[cur.preference_key] = cur.preference_value;
  //     return acc;
  //   }, {});
  //
  //   const mappedImage = preferenceData.data.reduce<Record<string, string>>((acc, cur) => {
  //     acc[cur.preference_key] = cur.preference_value2 ?? '';
  //     return acc;
  //   }, {});
  //
  //   setFormData({
  //     title: mapped.title || '',
  //     event_types: mapped.event_types ? JSON.parse(mapped.event_types) : [],
  //     camera_stream_enabled: mapped.camera_stream_enabled !== 'false',
  //   });
  //
  //   setFiles({
  //     login_logo: null,
  //     nav_logo: null,
  //     login_background: null,
  //   });
  //   setPreferences([]);
  //
  //   setPreferenceMap(mappedImage);
  // }, [preferenceData]);
  //
  // useEffect(() => {
  //   resetForm();
  // }, [resetForm]);
  //
  // const getFileName = (file: File | null, backendPath?: string) => {
  //   if (file) return file.name;
  //   if (backendPath) return backendPath.split('/').pop() || '';
  //   return '';
  // };
  //
  // const handleCancel = () => {
  //   resetForm();
  //   setButton({ submit: true, cancel: true });
  // };
  //
  // // DB section (postgres_conn_database / _username / _password) - kept for
  // // reference, not rendered. Those preferences are the RTU database credentials
  // // get_traffic_overview.py reads live; they are no longer editable here, so
  // // they must NOT be in the submit payload or the stored values would be wiped.
  // //
  // // const [dbConfig, setDbConfig] = useState({ database: '', user: '', password: '' });
  // //
  // // const handleDbChange = (
  // //   e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  // // ) => {
  // //   const { name, value } = e.target;
  // //   setDbConfig((prev) => ({ ...prev, [name]: value }));
  // //   setButton({ submit: false, cancel: false });
  // // };
  // //
  // // in resetForm():
  // //   setDbConfig({
  // //     database: mapped.postgres_conn_database || '',
  // //     user: mapped.postgres_conn_username || '',
  // //     password: mapped.postgres_conn_password || '',
  // //   });
  // //
  // // in handleSubmit():
  // //   const dbPreferences: PreferenceUpdateItem[] = [
  // //     { preference_key: 'postgres_conn_database', preference_value: dbConfig.database, preference_value2: '' },
  // //     { preference_key: 'postgres_conn_username', preference_value: dbConfig.user, preference_value2: '' },
  // //     { preference_key: 'postgres_conn_password', preference_value: dbConfig.password, preference_value2: '' },
  // //   ];
  // //   const data = [...formPreferences, ...dbPreferences, ...preferences];
  //
  // const handleSubmit = () => {
  //   const formPreferences: PreferenceUpdateItem[] = Object.entries(formData).map(
  //     ([key, value]) => ({
  //       preference_key: key,
  //       preference_value: Array.isArray(value)
  //         ? JSON.stringify(value)
  //         : value == null
  //           ? ''
  //           : String(value),
  //       preference_value2: Array.isArray(value)
  //         ? JSON.stringify(value)
  //         : value == null
  //           ? ''
  //           : String(value),
  //     })
  //   );
  //
  //   const data = [...formPreferences, ...preferences];
  //   const payload: UpdatePreferenceRequest = { data };
  //   updatePreferenceMutation.mutateAsync(payload);
  // };
  // ------------------------------------------------------------------------
  // end of UI section
  // ------------------------------------------------------------------------

  const markChanged = () => {
    if (isNewClickedRef.current) setHasChangesCreate(true);
    else setHasChangesUpdate(true);
    if (selectedIdRef.current) setHasChangesUpdate(true);
  };

  const selectRow = (row: Tabulator.RowComponent) => {
    const item = row.getData() as AppVersionListItem;
    tbRef.current?.deselectRow();
    row.select();
    row.getElement().classList.add('custom-hover');

    setSelectedId(item.app_id);
    setFormValues({ version: item.app_version, notes: item.release_notes ?? '' });
    setStoredFileName(item.apk_file_name ?? '');
    clearFileInput();
    setDisabledForm(false);
    setHasChangesUpdate(false);
    setHasChangesCreate(false);
    setIsNewClicked(false);
    enableUpdateButtons();

    requestAnimationFrame(() => {
      versionRef.current?.focus();
    });
  };

  const handleRowSelected = (row: Tabulator.RowComponent) => {
    if (hasChangesCreateRef.current || hasChangesUpdateRef.current) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), { mode: 'confirm' });
      message
        .confirmClicked()
        .then(() => selectRow(row))
        .catch(() => {
          row.deselect();
          const prevId = selectedIdRef.current;
          if (prevId) {
            const prevRow = tbRef.current?.getRow(prevId) || null;
            prevRow?.select();
            prevRow?.getElement().classList.add('custom-hover');
          }
        });
    } else {
      selectRow(row);
    }
  };

  const handleApkInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    markChanged();
  };

  const handleApkFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.apk')) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('pref>msg>invalid apk'));
      e.target.value = '';
      return;
    }
    if (file.size > APK_MAX_BYTES) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('pref>msg>apk too large'));
      e.target.value = '';
      return;
    }
    setApkFile(file);
    markChanged();
  };

  const startNew = () => {
    tbRef.current?.deselectRow();
    emptyDetail();
    setIsNewClicked(true);
    enableRegisterButtons();
    setSelectedId(null);
    setDisabledForm(false);
    setHasChangesUpdate(false);
    setHasChangesCreate(false);
    requestAnimationFrame(() => versionRef.current?.focus());
  };

  const handleNewButtonClick = () => {
    if (hasChangesCreate || hasChangesUpdate) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), { mode: 'confirm' });
      message.confirmClicked().then(startNew);
    } else {
      startNew();
    }
  };

  // Shared by Register and Confirm: version checked, notes trimmed, file only
  // when one is attached (Register requires it, Confirm treats it as replace).
  const buildFormData = (requireFile: boolean) => {
    const version = formValues.version.trim();
    if (!APP_VERSION_PATTERN.test(version)) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('pref>msg>version required'));
      return null;
    }
    if (requireFile && !apkFile) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('pref>msg>apk required'));
      return null;
    }
    const data = new FormData();
    data.append('app_version', version);
    data.append('release_notes', formValues.notes.trim());
    if (apkFile) data.append('apk', apkFile);
    return data;
  };

  const handleRegistButtonClick = () => {
    const data = buildFormData(true);
    if (data) uploadAppVersionMutation.mutate(data);
  };

  const handleConfirmButtonClick = () => {
    if (selectedId == null) return;
    const data = buildFormData(false);
    if (data) updateAppVersionMutation.mutate({ appId: selectedId, data });
  };

  const handleCancelButtonClick = () => {
    if (hasChangesUpdate || (isNewClicked && hasChangesCreate)) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), { mode: 'confirm' });
      message.confirmClicked().then(reloadCallback);
      return;
    }
    reloadCallback();
  };

  const handleDeleteButtonClick = () => {
    if (selectedId == null) return;
    // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
    const message = new NoticeMessage(t('msg > delete confirm'), { mode: 'confirm' });
    message.confirmClicked().then(() => {
      deleteAppVersionMutation.mutate(selectedId);
    });
  };

  const logs = selectedRelease
    ? [
        { label: t('cmn > registered by'), value: selectedRelease.registered_by ?? '' },
        { label: t('cmn > registered time'), value: formatReleasedAt(selectedRelease.released_at) },
      ]
    : [];

  const isMutating =
    uploadAppVersionMutation.isPending ||
    updateAppVersionMutation.isPending ||
    deleteAppVersionMutation.isPending;

  // Release table - same Tabulator setup as Language Management (No column,
  // centered cells, 10 per page, "Total N Results" footer).
  const releaseColumns = [
    {
      title: t('cmn > no'),
      formatter: (cell: Tabulator.CellComponent) => {
        const row = cell.getRow();
        const page = row.getTable().getPage();
        const pageSize = row.getTable().getPageSize();
        return (Number(page) - 1) * Number(pageSize) + row.getPosition(true);
      },
      width: 65,
      headerSort: false,
      ...commonColumnSettings,
    },
    {
      title: t('pref>col>file'),
      field: 'apk_file_name',
      widthGrow: 2,
      headerSort: false,
      formatter: (cell: Tabulator.CellComponent) => {
        const item = cell.getRow().getData() as AppVersionListItem;
        if (!item.apk_file_name) return '-';
        return escapeHtml(item.apk_file_name);
      },
      ...commonColumnSettings,
    },
    {
      title: t('pref>col>version'),
      field: 'app_version',
      widthGrow: 1,
      headerSort: false,
      formatter: (cell: Tabulator.CellComponent) => {
        const item = cell.getRow().getData() as AppVersionListItem;
        const version = escapeHtml(item.app_version);
        if (!item.is_latest) return version;
        return `${version} <span style="margin-left:6px;padding:1px 8px;border-radius:4px;font-size:11px;background:#dbeafe;color:#1d4ed8;">${escapeHtml(
          t('pref>label>latest')
        )}</span>`;
      },
      ...commonColumnSettings,
    },
    {
      title: t('pref>col>size'),
      field: 'apk_file_size',
      widthGrow: 1,
      headerSort: false,
      formatter: (cell: Tabulator.CellComponent) => formatFileSize(cell.getValue() ?? null),
      ...commonColumnSettings,
    },
    {
      title: t('pref>col>released at'),
      field: 'released_at',
      widthGrow: 1,
      headerSort: false,
      formatter: (cell: Tabulator.CellComponent) => formatReleasedAt(cell.getValue() ?? null),
      ...commonColumnSettings,
    },
    {
      title: t('pref>col>notes'),
      field: 'release_notes',
      width: '30px',
      headerSort: false,
      ...commonColumnSettings,
    },
    {
      title: t('pref>btn>download'),
      width: 90,
      cssClass: 'apk-download-cell',
      headerSort: false,
      formatter: (cell: Tabulator.CellComponent) => {
        const item = cell.getRow().getData() as AppVersionListItem;
        if (!item.has_apk) return '-';

        const label = escapeHtml(t('pref>btn>download'));
        return `<button type="button" aria-label="${label}" title="${label}" class="apk-download-button"><svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v12"></path><path d="m7 10 5 5 5-5"></path><path d="M5 21h14"></path></svg></button>`;
      },
      cellClick: (event: UIEvent, cell: Tabulator.CellComponent) => {
        event.stopPropagation();
        const item = cell.getRow().getData() as AppVersionListItem;
        if (item.has_apk) {
          window.location.assign(apkDownloadUrl(item.app_id));
        }
      },
      ...commonColumnSettings,
    },
  ];

  const releaseTabulatorLangs = () => ({
    pagination: {
      first: t('cmn > first page'),
      first_title: t('cmn > first page'),
      last: t('cmn > last page'),
      last_title: t('cmn > last page'),
      prev: t('cmn > page before'),
      prev_title: t('cmn > page before'),
      next: t('cmn > next page'),
      next_title: t('cmn > next page'),
    },
  });

  const releaseTableOptions = {
    debugInvalidOptions: true,
    pagination: true,
    movableRows: false,
    resizableRows: false,
    index: 'app_id',
    locale: 'ko',
    langs: { ko: releaseTabulatorLangs() },
    paginationSize: 10,
    selectableRows: false,
    rowHeight: 47,
    footerElement: `<div id="footer-bottom" style="padding: 0 20px 0 0; text-align: right;">${t(
      'cmn > total'
    )} ${releases.length} ${t('cmn > results')}</div>`,
    selectableRowsCheck: (row: Tabulator.RowComponent) =>
      !row.getElement().classList.contains('tabulator-selected'),
    placeholder: `<div style='text-align:center;'>${escapeHtml(t('pref>msg>no releases'))}</div>`,
  };

  return (
    <>
      <section className="wrap">
        <div className="header-title">
          {/* SYSTEM / SYSTEM-PREFERENCE are cm_code rows (Program Management's
              menu tree, postgres/initdb/50.cm_insert.sql) with real eng/ind/kor
              labels - the same tags every sibling management page's breadcrumb
              uses (see UserManagement.tsx, CodeManagement.tsx). */}
          <h3>{t('SYSTEM')}</h3>
          <h3>&gt;</h3>
          <h3>{t('SYSTEM-PREFERENCE')}</h3>
        </div>

        {/* UI section - hidden, kept for reference (see the note at the top of
            this file). NOTE: the DB block inside was itself a JSX comment; it is
            written plain here because JSX comments cannot nest - re-wrap it in
            a JSX comment when restoring this card.

        <ContainerCard className=" flex flex-col gap-[12px]!">
          <span className="title3medium pb-[20px]">UI</span>
          <div className="w-full flex flex-col gap-3">
            <DetailForm
              className="items-center!"
              label={t('pref>field>title')}
              value={formData.title}
              name="title"
              inputFormStyle="w-[620px]!"
              required={true}
              onChange={handleInputChange}
            />
          </div>
          <div className="flex flex-col gap-[25px]">
            <div className="w-fit flex flex-col gap-3 relative">
              <DetailForm
                className="items-center!"
                label={t('pref>field>login logo')}
                value={getFileName(files.login_logo, preferenceMap.login_logo)}
                name=""
                readonly={true}
                required={true}
                inputFormStyle="w-[620px]!"
              >
                <div className="w-full">
                  <input
                    type="file"
                    ref={loginLogoRef}
                    onChange={(e) => handleFileChange(e, 'login_logo')}
                    style={{ display: 'none' }}
                  />

                  <Button
                    onClick={() => loginLogoRef.current?.click()}
                    label={t('cmn > attach file')}
                    customButton="btn-search"
                  />
                </div>
                <span className="ml-2 text-[11px] font-[400] whitespace-nowrap text-[#575757] absolute bottom-[-18px] left-[180px]">
                  {t('pref>msg>upload hint')}
                </span>
              </DetailForm>
            </div>

            <div className="w-fit flex flex-col gap-3 relative">
              <DetailForm
                className="items-center!"
                label={t('pref>field>nav logo')}
                value={getFileName(files.nav_logo, preferenceMap.nav_logo)}
                name=""
                required={true}
                inputFormStyle="w-[620px]!"
                readonly={true}
              >
                <div className="w-full">
                  <input
                    type="file"
                    ref={navLogoRef}
                    onChange={(e) => handleFileChange(e, 'nav_logo')}
                    style={{ display: 'none' }}
                  />

                  <Button
                    label={t('cmn > attach file')}
                    customButton="btn-search"
                    onClick={() => navLogoRef.current?.click()}
                  />
                </div>
                <span className="ml-2 text-[11px] font-[400] whitespace-nowrap text-[#575757] absolute bottom-[-18px] left-[180px]">
                  {t('pref>msg>upload hint')}
                </span>
              </DetailForm>
            </div>

            <div className="w-fit flex flex-col gap-3 relative">
              <DetailForm
                className="items-center!"
                label={t('pref>field>login background')}
                value={getFileName(files.login_background, preferenceMap.login_background)}
                name=""
                required={true}
                inputFormStyle="w-[620px]!"
                readonly={true}
              >
                <div className="w-full">
                  <input
                    type="file"
                    ref={loginBackgroundRef}
                    onChange={(e) => handleFileChange(e, 'login_background')}
                    style={{ display: 'none' }}
                  />

                  <Button
                    label={t('cmn > attach file')}
                    customButton="btn-search"
                    onClick={() => loginBackgroundRef.current?.click()}
                  />
                </div>
                <span className="ml-2 text-[11px] font-[400] whitespace-nowrap text-[#575757] absolute bottom-[-18px] left-[180px]">
                  {t('pref>msg>upload hint')}
                </span>
              </DetailForm>
            </div>

            <div className="w-fit flex flex-row gap-x-4 items-center">
              <span className="title3medium w-[170px]">{t('pref>field>event type')}</span>
              {eventType.map((item) => (
                <CheckBox
                  key={item.value}
                  label={item.label}
                  value={item.value}
                  checked={formData.event_types.includes(item.value)}
                  onChange={handleCheckboxChange}
                />
              ))}
            </div>

            <div className="w-fit flex flex-row gap-x-4 items-center">
              <span className="title3medium w-[170px]">{t('pref>field>camera stream')}</span>
              <CheckBox
                label={t('cmn > use')}
                checked={formData.camera_stream_enabled}
                onChange={handleCameraToggleChange}
              />
            </div>
          </div>

          DB section - not rendered (see the note above handleSubmit):
          <span className="title3medium pb-[20px] pt-[20px] border-t border-gray-200 mt-4">DB</span>
          <div className="w-[810px] flex flex-col gap-3">
            <DetailForm
              className="items-center!"
              label="Database"
              value={dbConfig.database}
              name="database"
              inputFormStyle="w-full"
              required={true}
              onChange={handleDbChange}
            />
            <DetailForm
              className="items-center!"
              label="DB User"
              value={dbConfig.user}
              name="user"
              inputFormStyle="w-full"
              required={true}
              onChange={handleDbChange}
            />
            <DetailForm
              className="items-center!"
              label="DB Password"
              value={dbConfig.password}
              name="password"
              inputType="password"
              inputFormStyle="w-full"
              required={true}
              onChange={handleDbChange}
            />
          </div>

          <div className="flex flex-row gap-[12px] ml-auto mt-[40px]">
            <Button
              label={t('cmn > save')}
              customButton="btn-search"
              disabled={button.submit}
              onClick={handleSubmit}
            />
            <Button
              label={t('cmn > cancel')}
              customButton="btn-danger"
              disabled={button.cancel}
              onClick={handleCancel}
            />
          </div>
        </ContainerCard>
        */}

        {/* Release history on top, form below - the same order as every other
            management page (see LanguageManagement.tsx). Publishing here is
            what the driver app's update prompt and the landing page's Download
            button pick up (GET /app-version/latest, /download/latest). */}
        <ContainerCard>
          {/* react-tabulator only rebuilds when `data` changes, so the column
              titles would keep the language the table was first built in after
              the operator switches ID/EN. The key forces a rebuild - but it must
              sit on a WRAPPER, not on ReactTabulator itself: React removes the
              keyed node from the DOM before running the unmount effect, and
              Tabulator's destroy() calls unobserve(element.parentNode), which
              throws once the table's own div has no parent anymore. With the
              wrapper keyed, the table div keeps its parent while it is destroyed. */}
          <div key={i18n.language}>
            <ReactTabulator
              id="app-version-table"
              className="container-tabullator"
              data={releases}
              columns={releaseColumns as unknown as ColumnDefinition[]}
              layout={'fitDataStretch'}
              options={releaseTableOptions}
              onRef={(r: { current: Tabulator | null }) => {
                tbRef.current = r.current;
              }}
              events={{
                rowClick: (_e: UIEvent, row: Tabulator.RowComponent) => handleRowSelected(row),
                tableBuilt: () => {
                  if (selectedId != null) {
                    const row = tbRef.current?.getRow(selectedId) || null;
                    row?.select();
                    row?.getElement().classList.add('custom-hover');
                  } else if (newId != null) {
                    const row = tbRef.current?.getRow(newId) || null;
                    if (row) {
                      tbRef.current?.scrollToRow(row, 'top', true);
                      handleRowSelected(row);
                    }
                  }
                },
              }}
            />
          </div>
        </ContainerCard>

        <ContainerCard>
          <div className="flex w-full flex-col gap-[10px]">
            <span className="title3medium pb-[10px]">{t('pref>section>mobile app')}</span>
            <div className="flex flex-col gap-4">
              <DetailForm
                label={t('pref>field>app version')}
                value={formValues.version}
                inputType={'text'}
                onChange={handleApkInputChange}
                name={'version'}
                disabled={disabledForm}
                required
                formRef={versionRef}
              />

              <div className="flex w-full flex-col gap-2">
                <DetailForm
                  label={t('pref>field>apk file')}
                  value={apkFile?.name ?? storedFileName}
                  name=""
                  required
                  inputFormStyle="w-[620px]!"
                  readonly={true}
                  disabled={disabledForm}
                >
                  <div className="w-full">
                    <input
                      type="file"
                      accept=".apk,application/vnd.android.package-archive"
                      ref={apkFileRef}
                      onChange={handleApkFileChange}
                      style={{ display: 'none' }}
                    />

                    <Button
                      label={t('cmn > attach file')}
                      customButton="btn-search"
                      disabled={disabledForm}
                      onClick={() => apkFileRef.current?.click()}
                    />
                  </div>
                </DetailForm>
              </div>

              <div className="flex w-full flex-col gap-2">
                <DetailForm
                  className="items-start!"
                  label={t('pref>field>release notes')}
                  value={formValues.notes}
                  name={'notes'}
                  inputType="textarea"
                  styleTextArea="w-full! min-h-[120px]!"
                  onChange={handleApkInputChange}
                  disabled={disabledForm}
                />
                <span className="ml-[186px] text-[11px] font-[400] text-[#575757] dark:text-[#aab2bd]">
                  {selectedId != null ? t('pref>msg>apk replace hint') : t('pref>msg>apk hint')}
                </span>
              </div>
            </div>

            <hr className="border-t border-gray-300" />

            <div className="flex items-center justify-between gap-4 w-full">
              <div className="flex-1">{disabledForm ? null : <LogList logs={logs} />}</div>
              <div className="flex-none">
                <ButtonGroup
                  onClickDelete={handleDeleteButtonClick}
                  onClickNew={handleNewButtonClick}
                  onClickCancel={handleCancelButtonClick}
                  onClickRegist={handleRegistButtonClick}
                  onClickConfirm={handleConfirmButtonClick}
                  isNewClicked={isNewClicked}
                  cancelButtonState={buttonState.cancel}
                  confirmButtonState={hasChangesUpdate ? false : buttonState.confirm}
                  deleteButtonState={buttonState.delete}
                  newButtonState={hasChangesCreate ? false : buttonState.create}
                />
              </div>
            </div>
          </div>
        </ContainerCard>
      </section>
      {/* UI section: add `uploadPreferenceFileMutation.isPending ||` back when restoring. */}
      <Loader isOpen={isMutating} />
    </>
  );
};

export default PreferencesManagement;
